import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import useSubscriberLists from '../../hooks/useSubscriberLists';
import { OnlyFansLists } from '../../types/onlyFansTypes';
import { stripHtmlAndConvertParagraphs } from '../../utils/common';

/**
 * Props for the searchable multi-select component
 */
export interface ListMultiSelectProps {
    options: OnlyFansLists.List[];
    value: OnlyFansLists.List[];
    onChange: (next: OnlyFansLists.List[]) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    color?: 'primary' | 'secondary' | 'default' | 'error' | 'success' | 'info' | 'warning' | undefined;
    disabled?: boolean;
    loading?: boolean;
    /** When provided, the parent can react to search text changes (useful for server filtering). */
    onSearchChange?: (text: string) => void;
    /** Limit how many selected chips are shown before "+N" */
    limitTags?: number;
    /** Title shown in the Card header */
    title?: string;
}

// ---- Virtualized listbox for Autocomplete (react-window) ----
const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data as Array<React.ReactElement>;
    const item = dataSet[index];
    return React.cloneElement(item, {
        style: {
            ...style,
            top: (style as React.CSSProperties).top,
        },
    });
}

const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef<HTMLDivElement>(function OuterElementType(props, ref) {
    const other = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...other} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
    const { children, style, ...other } = props;
    const itemData = React.Children.toArray(children) as React.ReactElement[];
    const itemCount = itemData.length;
    const ITEM_SIZE = 56; // item height in px
    const height = Math.min(8, itemCount) * ITEM_SIZE + 2 * LISTBOX_PADDING;

    // Hide the outer listbox scrollbar; let react-window handle scrolling
    const outerStyle: React.CSSProperties = { ...(style as React.CSSProperties), overflow: 'hidden', padding: 0 };
    const virtualOuterStyle: React.CSSProperties = { padding: LISTBOX_PADDING };

    return (
        <div ref={ref} {...other} style={outerStyle}>
            <OuterElementContext.Provider value={{ ...other, style: virtualOuterStyle }}>
                <VariableSizeList
                    itemData={itemData}
                    height={height}
                    width="100%"
                    outerElementType={OuterElementType as any}
                    innerElementType="ul"
                    itemSize={() => ITEM_SIZE}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

/**
 * A searchable multi-select based on MUI Autocomplete with checkboxes.
 * Designed to handle ~400 options comfortably with client-side filtering.
 */
export default function ListMultiSelect({
    options,
    value,
    onChange,
    label = 'Lists',
    placeholder = 'Search lists…',
    size = 'small',
    color = 'primary',
    disabled,
    loading,
    onSearchChange,
    limitTags = 3,
    title = 'Onlyfans Lists',
}: ListMultiSelectProps) {
    // Local input state (so we can display the current query)
    const [inputValue, setInputValue] = React.useState('');
    const { refetch: refetchLists } = useSubscriberLists(true);

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ pt: 1 }}>
                <Stack justifyContent="space-between" direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {loading && <CircularProgress size={16} />}
                        <Typography fontWeight={600}>{title}</Typography>
                        <Chip size="small" label={value?.length ?? 0} color={value?.length === 0 ? 'default' : color} />
                    </Stack>

                    <Tooltip title="Reload Lists from OnlyFans">
                        <span>
                            <IconButton
                                disabled={loading}
                                color="secondary"
                                onClick={() => {
                                    refetchLists();
                                }}
                            >
                                <RefreshIcon fontSize="medium" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
                {loading ? (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <LinearProgress sx={{ flexGrow: 1 }} />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                            {options?.length ? options.length.toLocaleString() : 0}...
                        </Typography>
                    </Stack>
                ) : null}
                {!loading && (
                    <Autocomplete
                        multiple
                        ListboxComponent={ListboxComponent}
                        disableCloseOnSelect
                        filterSelectedOptions
                        disableClearable
                        options={options}
                        value={value}
                        size={size}
                        disabled={disabled}
                        loading={loading}
                        inputValue={inputValue}
                        onInputChange={(_, v) => {
                            setInputValue(v);
                            onSearchChange?.(v);
                        }}
                        getOptionLabel={o => o.name}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        renderInput={params => <TextField {...params} label={label} placeholder={placeholder} />}
                        renderTags={(selected, getTagProps) =>
                            selected.map((opt, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={opt.id}
                                    label={stripHtmlAndConvertParagraphs(opt.name)}
                                    size={size}
                                    color={color}
                                />
                            ))
                        }
                        limitTags={limitTags}
                        noOptionsText={inputValue ? 'No matching lists' : 'No lists'}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" noWrap fontWeight={600} title={option.name}>
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.usersCount} users
                                        </Typography>
                                    </Box>
                                </Stack>
                            </li>
                        )}
                        onChange={(_, next) => onChange(next)}
                    />
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Example usage (remove if you don't want inline demo)
 *
 * const [selected, setSelected] = React.useState<OFList[]>([]);
 * return (
 *   <ListMultiSelect
 *     options={allLists}
 *     value={selected}
 *     onChange={setSelected}
 *   />
 * );
 */
